/**
 * Generic widget renderer — switches on `data_kind` returned by the
 * backend (`/api/widgets/data` or per-dashboard `/render`).
 */
import React from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { formatCompactMoney, formatMoney } from "@/lib/api";

const PIE_COLORS = ["#0B1A30", "#F28C24", "#475569", "#1E3A5F", "#F4A261", "#8A95A5", "#D97706", "#475880", "#94A3B8", "#FB923C", "#0EA5E9"];

const tooltipStyle = {
  background: "#0B1A30",
  border: "none",
  borderRadius: 4,
  color: "#fff",
  fontSize: 12,
};

export default function Widget({ widget }) {
  const title = widget.title || widget.name || widget.widget_type;
  return (
    <div
      data-testid={`widget-${widget.widget_type}`}
      className="flex h-full flex-col rounded-md border border-slate-200 bg-white shadow-sm"
    >
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <div>
          <div className="font-display text-sm font-semibold tracking-tight text-navy-600">
            {title}
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400">
            {widget.widget_type}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-3">
        <Body widget={widget} />
      </div>
    </div>
  );
}

function Body({ widget }) {
  const { data_kind, data, error } = widget;
  if (error) {
    return (
      <div className="text-sm text-red-600" data-testid="widget-error">
        {String(error)}
      </div>
    );
  }
  if (!data) return <div className="text-sm text-slate-400">No data</div>;
  if (data_kind === "kpi") return <KpiBody data={data} />;
  if (data_kind === "bar_grouped") return <BarGroupedBody data={data} />;
  if (data_kind === "bar_simple") return <BarSimpleBody data={data} />;
  if (data_kind === "pie") return <PieBody data={data} />;
  if (data_kind === "line_multi") return <LineBody data={data} />;
  if (data_kind === "table") return <TableBody data={data} />;
  if (data_kind === "pivot") return <PivotBody data={data} />;
  if (data_kind === "pv_mix") return <PvMixBody data={data} />;
  if (data_kind === "pv_summary") return <PvSummaryBody data={data} />;
  return <pre className="text-xs">{JSON.stringify(data, null, 2)}</pre>;
}

function KpiBody({ data }) {
  const isPercent = data.suffix === "%";
  const isMonths = data.suffix === "mo";
  const display = isPercent
    ? `${data.value.toFixed(1)}%`
    : isMonths
    ? `${data.value.toFixed(1)}`
    : formatCompactMoney(data.value);
  return (
    <div className="flex h-full flex-col justify-center">
      <div className="flex items-center gap-2 text-brand-orange">
        <TrendingUp className="h-3.5 w-3.5" />
        <span className="text-[10px] font-semibold uppercase tracking-widest">
          {data.count != null ? `${data.count} records` : "metric"}
        </span>
      </div>
      <div className="mt-1.5 font-display text-3xl font-semibold tabular text-navy-600">
        {display}
        {isMonths && <span className="ml-1.5 text-base font-medium text-slate-500">{data.suffix}</span>}
      </div>
    </div>
  );
}

function BarGroupedBody({ data }) {
  return (
    <div className="h-[220px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.rows || []} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="x" tick={{ fill: "#475569", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
          <YAxis tick={{ fill: "#475569", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={formatCompactMoney} />
          <Tooltip cursor={{ fill: "rgba(11,26,48,.04)" }} contentStyle={tooltipStyle} formatter={formatMoney} />
          <Legend wrapperStyle={{ fontSize: 10, color: "#475569" }} />
          {(data.series || []).map((s, i) => (
            <Bar key={s} dataKey={s} fill={i === 0 ? "#0B1A30" : "#F28C24"} radius={[2, 2, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function BarSimpleBody({ data }) {
  return (
    <div className="h-[220px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data.items || []} layout="vertical" margin={{ top: 0, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
          <XAxis type="number" tick={{ fill: "#475569", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={formatCompactMoney} />
          <YAxis type="category" dataKey="label" tick={{ fill: "#475569", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} width={90} />
          <Tooltip contentStyle={tooltipStyle} formatter={formatMoney} />
          <Bar dataKey="value" fill="#F28C24" radius={[0, 2, 2, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function PieBody({ data }) {
  return (
    <div className="h-[220px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie data={data.items || []} dataKey="value" nameKey="label" innerRadius={36} outerRadius={80} paddingAngle={2}>
            {(data.items || []).map((_, i) => (
              <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
            ))}
          </Pie>
          <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [formatMoney(v), n]} />
          <Legend wrapperStyle={{ fontSize: 10, color: "#475569" }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

function LineBody({ data }) {
  return (
    <div className="h-[220px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data.rows || []}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
          <XAxis dataKey="x" tick={{ fill: "#475569", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
          <YAxis tick={{ fill: "#475569", fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={formatCompactMoney} />
          <Tooltip contentStyle={tooltipStyle} formatter={formatMoney} />
          {(data.series || []).map((s, i) => (
            <Line key={s} type="monotone" dataKey={s} stroke={i === 0 ? "#F28C24" : "#0B1A30"} strokeWidth={2} dot={false} />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function TableBody({ data }) {
  const cols = data.columns || [];
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
            {cols.map((c) => (
              <th key={c} className="px-2 py-2 text-left">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(data.rows || []).map((r, i) => (
            <tr key={r.id || i} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
              {cols.map((c) => (
                <td key={c} className="px-2 py-1.5 text-slate-700">
                  {c === "amount"
                    ? formatMoney(r.amount, r.currency)
                    : c === "date" && r.date
                    ? new Date(r.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                    : String(r[c] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PivotBody({ data }) {
  return (
    <div className="overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-slate-200 bg-navy-600 text-white">
            <th className="sticky left-0 z-10 bg-navy-600 px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-widest">Company</th>
            {(data.cols || []).map((c) => (
              <th key={c} className="px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-widest">{c}</th>
            ))}
            <th className="bg-brand-orange px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-widest">Total</th>
          </tr>
        </thead>
        <tbody>
          {(data.rows || []).map((row, ri) => (
            <tr key={row} className="border-b border-slate-100 last:border-0 hover:bg-slate-50">
              <td className="sticky left-0 z-10 bg-white px-2 py-1.5 font-medium text-navy-600">{row}</td>
              {(data.cols || []).map((c, ci) => (
                <td key={c} className="px-2 py-1.5 text-right tabular text-slate-700">
                  {data.matrix[ri][ci] ? formatCompactMoney(data.matrix[ri][ci]) : "—"}
                </td>
              ))}
              <td className="bg-orange-50 px-2 py-1.5 text-right tabular font-semibold text-brand-orange">
                {formatCompactMoney(data.row_totals?.[ri] || 0)}
              </td>
            </tr>
          ))}
          <tr className="border-t-2 border-navy-600 bg-slate-50 font-semibold">
            <td className="sticky left-0 z-10 bg-slate-50 px-2 py-1.5 text-navy-600">TOTAL</td>
            {(data.col_totals || []).map((v, i) => (
              <td key={i} className="px-2 py-1.5 text-right tabular text-navy-600">
                {formatCompactMoney(v)}
              </td>
            ))}
            <td className="bg-brand-orange px-2 py-1.5 text-right tabular text-white">
              {formatCompactMoney(data.grand_total)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}


/* ---------- Price / Volume -------------------------------------------------
 * pv_mix     — monthly grouped bar chart, values are % of prior-year month.
 * pv_summary — two-band table: $ amounts (top) and % of prior-year sales
 *              (bottom), across Month / LTM / YTD columns.
 * Navy = Volume, Orange = Price, Slate = Mix interaction.
 */
const PV_COLORS = {
  "Volume Effect": "#0B1A30",
  "Price Effect": "#F28C24",
  "Mix": "#7C8CA6",
};

function PvMixBody({ data }) {
  const rows = data.rows || [];
  const series = data.series || ["Volume Effect", "Price Effect", "Mix"];
  return (
    <div className="flex h-full flex-col">
      {data.division_name && (
        <div className="mb-1.5 px-1 text-[10px] uppercase tracking-widest text-slate-500">
          Division: <span className="font-semibold text-navy-600">{data.division_name}</span>
          {data.anchor_month && <span className="ml-3 text-slate-400">· through {data.anchor_month}</span>}
        </div>
      )}
      <div className="h-[260px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
            <XAxis dataKey="x" tick={{ fill: "#475569", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#E2E8F0" }} />
            <YAxis
              tick={{ fill: "#475569", fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v.toFixed(1)}%`}
            />
            <Tooltip
              cursor={{ fill: "rgba(11,26,48,.04)" }}
              contentStyle={tooltipStyle}
              formatter={(v, name) => [`${Number(v).toFixed(2)}%`, name]}
            />
            <Legend wrapperStyle={{ fontSize: 10, color: "#475569" }} />
            {series.map((s) => (
              <Bar key={s} dataKey={s} fill={PV_COLORS[s] || "#475569"} radius={[2, 2, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function _fmtMoneyShort(v) {
  if (v == null || Number.isNaN(v)) return "—";
  // Table header says "$ in 000s" — always render in thousands.
  const k = v / 1000;
  const abs = Math.abs(k);
  const fmt = abs >= 100 ? abs.toFixed(0) : abs.toFixed(1);
  const withSep = Number(fmt).toLocaleString("en-US", { maximumFractionDigits: abs >= 100 ? 0 : 1 });
  return v < 0 ? `(${withSep})` : withSep;
}

function _fmtPct(v) {
  if (v == null || Number.isNaN(v)) return "—";
  const abs = Math.abs(v);
  const sign = v < 0 ? "(" : "";
  const close = v < 0 ? ")" : "";
  return `${sign}${abs.toFixed(1)}%${close}`;
}

function PvSummaryBody({ data }) {
  const cols = data.columns || [];
  const rowDefs = data.rows || [];
  const borderBottom = (key) =>
    key === "interaction" ? "border-b-2 border-navy-600" : "border-b border-slate-100";
  const isTotal = (key) => key === "current_sales" || key === "prior_sales";

  return (
    <div className="flex h-full flex-col">
      {data.division_name && (
        <div className="mb-1.5 px-1 text-[10px] uppercase tracking-widest text-slate-500">
          Division: <span className="font-semibold text-navy-600">{data.division_name}</span>
          {data.anchor_month && <span className="ml-3 text-slate-400">· anchor {data.anchor_month}</span>}
        </div>
      )}

      {/* $ amounts band */}
      <table className="w-full text-xs tabular">
        <thead>
          <tr className="border-b border-slate-200 bg-navy-600 text-white">
            <th className="px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-widest">
              Price Volume Analysis — Annual Summary
            </th>
            <th className="px-2 py-2 text-right text-[10px] font-semibold italic text-slate-300">
              ($ in 000s)
            </th>
            {cols.map((c) => (
              <th key={c.key} className="px-2 py-2 text-right text-[10px] font-semibold uppercase tracking-widest">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rowDefs.map((r) => (
            <tr key={r.key} className={`${borderBottom(r.key)} ${isTotal(r.key) ? "bg-slate-50 font-semibold text-navy-600" : "text-slate-700"}`}>
              <td className="px-2 py-1.5" colSpan={2}>
                {r.label}
              </td>
              {cols.map((c) => (
                <td key={c.key} className="px-2 py-1.5 text-right">
                  {_fmtMoneyShort(c.dollars?.[r.key])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* % of Prior-Year Sales band */}
      <div className="mt-3">
        <div className="px-2 py-1 text-[10px] font-semibold italic text-slate-500 underline">
          % of Prior Year Sales
        </div>
        <table className="w-full text-xs tabular">
          <tbody>
            {rowDefs.map((r) => {
              const isPy = r.key === "prior_sales";
              const label = isPy ? "Prior Year Revenue" : r.label;
              return (
                <tr key={`pct-${r.key}`} className={`${borderBottom(r.key)} ${isTotal(r.key) ? "bg-orange-50 font-semibold text-navy-600" : "text-slate-700"}`}>
                  <td className="px-2 py-1.5" colSpan={2}>
                    {label}
                  </td>
                  {cols.map((c) => {
                    const v = c.percent?.[r.key];
                    const tone = v != null && v < 0 ? "text-red-600" : "";
                    const strong = isTotal(r.key) ? "font-semibold italic" : "italic";
                    return (
                      <td key={c.key} className={`px-2 py-1.5 text-right ${tone} ${strong}`}>
                        {_fmtPct(v)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
